import "@/App.css";
import { useEffect, useRef } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { LayoutProvider } from "@/context/LayoutContext";
import { ChatProvider } from "@/context/ChatContext";
import { apiError } from "@/lib/api";
import Layout from "@/components/Layout";
import Login from "@/pages/Login";
import Workspace from "@/pages/Workspace";
import History from "@/pages/History";
import QuoteDetail from "@/pages/QuoteDetail";
import Products from "@/pages/Products";
import FreightClasses from "@/pages/FreightClasses";
import FakRules from "@/pages/FakRules";
import Rules from "@/pages/Rules";
import Analytics from "@/pages/Analytics";
import Validation from "@/pages/Validation";
import Integrations from "@/pages/Integrations";
import Users from "@/pages/Users";
import LiveMap from "@/pages/LiveMap";
import Customers from "@/pages/Customers";
import CustomerDetail from "@/pages/CustomerDetail";
import Messages from "@/pages/Messages";
import CalendarPage from "@/pages/Calendar";
import Profile from "@/pages/Profile";
import ApiAccess from "@/pages/ApiAccess";
import Shipments from "@/pages/Shipments";
import BookNow from "@/pages/BookNow";
import ShipOrigins from "@/pages/ShipOrigins";
import UpchargeRules from "@/pages/UpchargeRules";
import PublicTracking from "@/pages/PublicTracking";
import Requests from "@/pages/Requests";

function Protected({ children }) {
  const { user } = useAuth();
  if (user === undefined)
    return (
      <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
        <div className="animate-pulse font-heading tracking-wide">Loading command center…</div>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <Navigate to="/" replace />;
  return children;
}

// Handles the Emergent Google OAuth return (URL fragment #session_id=...).
function AuthCallback() {
  const { processSession } = useAuth();
  const navigate = useNavigate();
  const ran = useRef(false);
  useEffect(() => {
    if (ran.current) return;
    ran.current = true;
    const m = window.location.hash.match(/session_id=([^&]+)/);
    if (!m) {
      navigate("/login", { replace: true });
      return;
    }
    processSession(decodeURIComponent(m[1]))
      .then(() => {
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/", { replace: true });
      })
      .catch((e) => {
        window.history.replaceState(null, "", window.location.pathname);
        navigate("/login?error=" + encodeURIComponent(apiError(e)), { replace: true });
      });
  }, []); // eslint-disable-line
  return (
    <div className="min-h-screen flex items-center justify-center bg-background text-muted-foreground">
      <div className="animate-pulse font-heading tracking-wide">Signing you in…</div>
    </div>
  );
}

function AppRoutes() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/track" element={<PublicTracking />} />
      <Route path="/track/:token" element={<PublicTracking />} />
      <Route path="/" element={<Protected><Layout /></Protected>}>
        <Route index element={<LiveMap />} />
        <Route path="workspace" element={<Workspace />} />
        <Route path="history" element={<History />} />
        <Route path="customers" element={<Customers />} />
        <Route path="customers/:company" element={<CustomerDetail />} />
        <Route path="messages" element={<Messages />} />
        <Route path="calendar" element={<CalendarPage />} />
        <Route path="requests" element={<Requests />} />
        <Route path="profile" element={<Profile />} />
        <Route path="quotes/:id" element={<QuoteDetail />} />
        <Route path="analytics" element={<Analytics />} />
        <Route path="products" element={<AdminOnly><Products /></AdminOnly>} />
        <Route path="freight-classes" element={<AdminOnly><FreightClasses /></AdminOnly>} />
        <Route path="fak-rules" element={<AdminOnly><FakRules /></AdminOnly>} />
        <Route path="rules" element={<AdminOnly><Rules /></AdminOnly>} />
        <Route path="upcharge-rules" element={<AdminOnly><UpchargeRules /></AdminOnly>} />
        <Route path="validation" element={<AdminOnly><Validation /></AdminOnly>} />
        <Route path="integrations" element={<AdminOnly><Integrations /></AdminOnly>} />
        <Route path="ship-origins" element={<AdminOnly><ShipOrigins /></AdminOnly>} />
        <Route path="developer" element={<AdminOnly><ApiAccess /></AdminOnly>} />
        <Route path="shipments" element={<Shipments />} />
        <Route path="book-now" element={<BookNow />} />
        <Route path="users" element={<AdminOnly><Users /></AdminOnly>} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AuthProvider>
        <LayoutProvider>
          <BrowserRouter>
            <ChatProvider>
              <AppRoutes />
            </ChatProvider>
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </LayoutProvider>
      </AuthProvider>
    </div>
  );
}

export default App;
